import * as Y from 'yjs'
import type { Edge, Node } from '../../../core/models/scene'
import { extractSceneFromYDoc } from '../../orgchart/services/DiagramForkService'
import { buildAllowedAttrKeys, filterNodeAttrs } from './fieldVisibility'
import type { FieldVisibilityConfig } from './fieldVisibility'
import {
  isPublicViewerEdge,
  PUBLIC_VIEWER_FIELD_VISIBILITY,
} from './publicViewerPolicy'
import {
  ORG_NODE_TYPES,
  truncate,
  pickRecordValue,
} from '../services/ViewerSnapshotService'
import type {
  ViewerNode,
  ViewerEdge,
  ViewerSnapshotPayload,
} from '../models/ViewerSnapshot'

const MAX_SCENE_NODES = 2000
const VIEWER_PRIVATE_CONTAINER_KEYS = new Set([
  'dataBinding',
  'cachedRecord',
  'localChanges',
  'boundRecordState',
])

export class EmptyViewerSnapshotError extends Error {
  constructor(message = 'Nothing to publish yet. Wait for the chart to finish loading, then try again.') {
    super(message)
    this.name = 'EmptyViewerSnapshotError'
  }
}

function filterViewerRecord(
  record: Record<string, unknown>,
  allowedKeys: Set<string>,
): Record<string, unknown> {
  const filtered = filterNodeAttrs(record, allowedKeys)

  for (const key of VIEWER_PRIVATE_CONTAINER_KEYS) {
    delete filtered[key]
  }

  if (filtered.customFields && typeof filtered.customFields === 'object' && !Array.isArray(filtered.customFields)) {
    const customFields = filterViewerRecord(filtered.customFields as Record<string, unknown>, allowedKeys)
    if (Object.keys(customFields).length > 0) {
      filtered.customFields = customFields
    } else {
      delete filtered.customFields
    }
  }

  return filtered
}

function countOrgNodes(nodes: readonly Node[]): number {
  return nodes.reduce((count, node) => (ORG_NODE_TYPES.has(node.type) ? count + 1 : count), 0)
}

function readPresentationState(doc: Y.Doc): {
  frames: unknown[]
  viewRulesData: Record<string, unknown>
  settingsData: unknown
  boundRecordStateMap: Y.Map<unknown>
} {
  const framesMap = doc.getMap('frames')
  const frames: unknown[] = []
  framesMap.forEach((record) => {
    frames.push(record)
  })

  const viewRulesMap = doc.getMap('viewRules')
  const viewRulesData: Record<string, unknown> = {}
  for (const key of ['rules', 'customPresets', 'presets']) {
    const val = viewRulesMap.get(key)
    if (typeof val === 'string') {
      try { viewRulesData[key] = JSON.parse(val) } catch { /* skip malformed */ }
    }
  }

  const settingsMap = doc.getMap('diagramSettings')
  const settingsJson = settingsMap.get('settings')
  let settingsData: unknown
  if (typeof settingsJson === 'string') {
    try { settingsData = JSON.parse(settingsJson) } catch { /* ignore */ }
  }

  return {
    frames,
    viewRulesData,
    settingsData,
    boundRecordStateMap: doc.getMap('boundRecordState'),
  }
}

function enrichNodesWithBoundRecords(
  nodes: Node[],
  boundRecordStateMap: Y.Map<unknown>,
): Node[] {
  return nodes.map((node) => {
    const props = node.props as Record<string, unknown> | undefined
    const binding = props?.dataBinding as {
      sourceId?: string
      recordId?: string
      cachedRecord?: Record<string, unknown>
      localChanges?: { fields?: Record<string, unknown> }
    } | undefined
    if (!binding?.cachedRecord) return node

    const merged: Record<string, unknown> = { ...binding.cachedRecord }

    if (binding.localChanges?.fields) {
      Object.assign(merged, binding.localChanges.fields)
    }

    if (binding.sourceId && binding.recordId) {
      const brKey = `${binding.sourceId}:${binding.recordId}`
      const brState = boundRecordStateMap.get(brKey) as
        | { localChanges?: Record<string, unknown> }
        | undefined
      if (brState?.localChanges) {
        Object.assign(merged, brState.localChanges)
      }
    }

    const enrichedAttrs = { ...merged, ...(node.attrs as Record<string, unknown>) }
    return { ...node, attrs: enrichedAttrs }
  })
}

function buildOrgPayloadFromScene(params: {
  nodes: Node[]
  edges: Edge[]
  diagramTitle?: string
  frames: unknown[]
  viewRulesData: Record<string, unknown>
  settingsData: unknown
  boundRecordStateMap: Y.Map<unknown>
}): OrgChartViewerPayload {
  const fieldVisibility = PUBLIC_VIEWER_FIELD_VISIBILITY
  const enrichedNodes = enrichNodesWithBoundRecords(params.nodes, params.boundRecordStateMap)

  const orgNodes = enrichedNodes
    .filter((node) => ORG_NODE_TYPES.has(node.type))
    .map((node): ViewerNode => ({
      id: node.id,
      x: Number.isFinite(node.x) ? node.x : 0,
      y: Number.isFinite(node.y) ? node.y : 0,
      name: pickRecordValue(node.attrs, ['name']) ?? pickRecordValue(node.props, ['name']) ?? truncate(node.name, 200) ?? truncate(node.text, 200),
      title: pickRecordValue(node.attrs, ['title', 'role']) ?? pickRecordValue(node.props, ['title', 'role']),
      department: pickRecordValue(node.attrs, ['department']) ?? pickRecordValue(node.props, ['department']),
      color: pickRecordValue(node.attrs, ['color', 'fill']) ?? pickRecordValue(node.props, ['color', 'fill']),
    }))
  const orgNodeIds = new Set(orgNodes.map((n) => n.id))
  const orgEdges = params.edges
    .filter((edge) => orgNodeIds.has(edge.from) && orgNodeIds.has(edge.to))
    .filter(isPublicViewerEdge)
    .map((edge): ViewerEdge => ({
      from: edge.from,
      to: edge.to,
      type: truncate(edge.type, 80) ?? pickRecordValue(edge.attrs, ['subtype', 'type']),
    }))

  let scenePayload: OrgChartViewerPayload['scene'] | undefined
  if (enrichedNodes.length <= MAX_SCENE_NODES) {
    const allowedKeys = buildAllowedAttrKeys(fieldVisibility)
    const filteredSceneNodes = enrichedNodes.map((node) => ({
      ...node,
      attrs: node.attrs ? filterViewerRecord(node.attrs as Record<string, unknown>, allowedKeys) : node.attrs,
      props: node.props ? filterViewerRecord(node.props as Record<string, unknown>, allowedKeys) : node.props,
    }))

    scenePayload = {
      diagramType: 'org',
      nodes: filteredSceneNodes,
      edges: params.edges.filter(isPublicViewerEdge),
      frames: params.frames.length > 0 ? params.frames : undefined,
      viewRules: Object.keys(params.viewRulesData).length > 0 ? params.viewRulesData : undefined,
      settings: params.settingsData,
    }
  }

  return {
    title: truncate(params.diagramTitle, 200),
    nodes: orgNodes,
    edges: orgEdges,
    fieldVisibility,
    scene: scenePayload,
  }
}

export interface OrgChartViewerPayload {
  title?: string
  nodes: ViewerNode[]
  edges: ViewerEdge[]
  fieldVisibility: FieldVisibilityConfig
  scene?: ViewerSnapshotPayload['scene']
}

export function buildOrgChartViewerPayload(params: {
  doc: Y.Doc
  diagramTitle?: string
  fieldVisibility: FieldVisibilityConfig
  /**
   * Rendered canvas scene. Used when the Y.Doc has no org shapes — e.g. Atlas
   * people/position live charts that project into the store but never write
   * canonical nodes into the diagram document.
   */
  canvasScene?: {
    nodes: Node[]
    edges: Edge[]
  }
}): OrgChartViewerPayload {
  const { doc, diagramTitle, canvasScene } = params
  const presentation = readPresentationState(doc)
  const ydocScene = extractSceneFromYDoc(doc)
  const ydocOrgCount = countOrgNodes(ydocScene.nodes)
  const canvasOrgCount = canvasScene ? countOrgNodes(canvasScene.nodes) : 0
  const useCanvasScene = ydocOrgCount === 0 && canvasOrgCount > 0 && canvasScene

  return buildOrgPayloadFromScene({
    nodes: useCanvasScene ? canvasScene.nodes : ydocScene.nodes,
    edges: useCanvasScene ? canvasScene.edges : ydocScene.edges,
    diagramTitle,
    frames: presentation.frames,
    viewRulesData: presentation.viewRulesData,
    settingsData: presentation.settingsData,
    boundRecordStateMap: presentation.boundRecordStateMap,
  })
}

export function assertNonEmptyOrgViewerPayload(payload: OrgChartViewerPayload): void {
  const sceneNodes = Array.isArray(payload.scene?.nodes) ? payload.scene.nodes : payload.nodes
  if (!Array.isArray(sceneNodes) || sceneNodes.length === 0) {
    throw new EmptyViewerSnapshotError()
  }
}

export interface GenogramViewerPayload {
  title?: string
  nodes: ViewerNode[]
  edges: ViewerEdge[]
  scene: ViewerSnapshotPayload['scene']
}

export function buildGenogramViewerPayload(params: {
  doc: Y.Doc
  diagramTitle?: string
}): GenogramViewerPayload {
  const { doc, diagramTitle } = params
  const scene = extractSceneFromYDoc(doc)

  const framesMap = doc.getMap('frames')
  const frames: unknown[] = []
  framesMap.forEach((record) => { frames.push(record) })

  const viewRulesMap = doc.getMap('viewRules')
  const viewRulesData: Record<string, unknown> = {}
  for (const key of ['rules', 'customPresets', 'presets']) {
    const val = viewRulesMap.get(key)
    if (typeof val === 'string') {
      try { viewRulesData[key] = JSON.parse(val) } catch { /* skip malformed */ }
    }
  }

  // Extract diagram settings
  const settingsMap = doc.getMap('diagramSettings')
  const settingsJson = settingsMap.get('settings')
  let settingsData: unknown
  if (typeof settingsJson === 'string') {
    try { settingsData = JSON.parse(settingsJson) } catch { /* ignore */ }
  }

  const personNodes = scene.nodes
    .filter((node) => node.type === 'person')
    .map((node): ViewerNode => ({
      id: node.id,
      x: Number.isFinite(node.x) ? node.x : 0,
      y: Number.isFinite(node.y) ? node.y : 0,
      name: pickRecordValue(node.attrs, ['name']) ?? truncate(node.name, 200),
    }))
  const personIds = new Set(personNodes.map((n) => n.id))
  const personEdges = scene.edges
    .filter((edge) => personIds.has(edge.from) && personIds.has(edge.to))
    .map((edge): ViewerEdge => ({
      from: edge.from,
      to: edge.to,
      type: truncate(edge.type, 80),
    }))

  return {
    title: truncate(diagramTitle, 200),
    nodes: personNodes,
    edges: personEdges,
    scene: {
      diagramType: 'genogram',
      nodes: scene.nodes,
      edges: scene.edges,
      frames: frames.length > 0 ? frames : undefined,
      viewRules: Object.keys(viewRulesData).length > 0 ? viewRulesData : undefined,
      settings: settingsData,
    },
  }
}
